import { Participant } from '../models';
import { MatchRepository, ParticipantRepository } from '../repositories';

export interface RoundRobinStanding {
  participant: Participant;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
}

export class GetRoundRobinStandingsUseCase {
  constructor(
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(tournamentId: string): Promise<RoundRobinStanding[]> {
    const participants = await this.participantRepository.getByTournamentId(tournamentId);
    const matches = await this.matchRepository.getByTournamentId(tournamentId);

    // Initialize standings
    const standings: Map<string, RoundRobinStanding> = new Map();
    
    participants.forEach(participant => {
      standings.set(participant.id, {
        participant,
        played: 0,
        won: 0,
        drawn: 0,
        lost: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDifference: 0,
        points: 0,
      });
    });

    // Process completed matches
    matches
      .filter(match => match.status === 'completed')
      .forEach(match => {
        const p1Standing = standings.get(match.participant1Id!);
        const p2Standing = standings.get(match.participant2Id!);

        if (!p1Standing || !p2Standing) return;

        const score1 = match.scoreParticipant1 ?? 0;
        const score2 = match.scoreParticipant2 ?? 0;

        // Update played
        p1Standing.played++;
        p2Standing.played++;

        // Update goals
        p1Standing.goalsFor += score1;
        p1Standing.goalsAgainst += score2;
        p2Standing.goalsFor += score2;
        p2Standing.goalsAgainst += score1;

        // Determine result
        if (score1 > score2) {
          // Participant 1 wins
          p1Standing.won++;
          p1Standing.points += 3;
          p2Standing.lost++;
        } else if (score2 > score1) {
          // Participant 2 wins
          p2Standing.won++;
          p2Standing.points += 3;
          p1Standing.lost++;
        } else {
          // Draw
          p1Standing.drawn++;
          p1Standing.points += 1;
          p2Standing.drawn++;
          p2Standing.points += 1;
        }

        // Update goal difference
        p1Standing.goalDifference = p1Standing.goalsFor - p1Standing.goalsAgainst;
        p2Standing.goalDifference = p2Standing.goalsFor - p2Standing.goalsAgainst;
      });

    // Convert to array and sort
    const standingsArray = Array.from(standings.values());
    
    standingsArray.sort((a, b) => {
      // Sort by points (descending)
      if (b.points !== a.points) return b.points - a.points;
      // Then by goal difference (descending)
      if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
      // Then by goals for (descending)
      if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
      // Finally alphabetically
      return a.participant.name.localeCompare(b.participant.name);
    });

    return standingsArray;
  }
}